import React from 'react'
import styled from '@emotion/styled'
import get from 'lodash/get'

import mq from '../styles/breakpoints'

import { Heading } from '../components/typography'
import BlogPost from '../components/BlogPost'
import Section from '../components/Section'
import { useLocale } from '../context/LocaleContextProvider'
import { IBlogInfo } from '../graphql/modules/blog'
import { IBlogsSection } from '../graphql/sections/blogs'
import { filterRenderableBlogCards } from '../helpers/blogCards'

interface IProps {
  data: IBlogsSection
}

const BlogsContainer = styled.div`
  ${mq({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: ['center', 'space-evenly', 'space-evenly'],
    marginTop: ['28px', '28px', '32px'],
  })}
`

export const BlogsSection: React.FC<IProps> = ({ data }) => {
  const { isFrench, localizeHref } = useLocale()
  // French pages only show posts that have a French page: localizeHref leaves the link unchanged when there's none.
  const isShownInLocale = (blog: IBlogInfo) => !isFrench || localizeHref(blog.link.link) !== blog.link.link
  // Incomplete drafts and posts without a French page are dropped before the top-up below, so a section
  // still shows three cards when one of the chosen blogs is an unfinished draft.
  const blogs = filterRenderableBlogCards(get(data, 'blogs', [])).filter(isShownInLocale)
  const title = get(data, 'title', '')
  const relatedBlogs = filterRenderableBlogCards(get(data, 'relatedBlogs', [])).filter(isShownInLocale)
  //blogs array comes from the Blogs section component in contentful. When less than three blogs are chosen, the rest can be filled in
  //by the related blogs, which are pulled from Blog Categories on the Blog section. Related blogs will be empty when this component is used on
  //a page that does not contain a Blog post.
  const displayBlogs = [...blogs]
  if (displayBlogs.length < 3 && relatedBlogs.length > 0) {
    displayBlogs.push(...relatedBlogs.slice(0, 3 - displayBlogs.length))
  }
  // No French posts left: hide the section rather than show a heading without cards.
  if (isFrench && displayBlogs.length === 0) return null

  return (
    <Section title={title}>
      <Heading as="h2">{title}</Heading>
      <BlogsContainer>
        {displayBlogs.map(blog => (
          <BlogPost key={blog.title} blog={blog} />
        ))}
      </BlogsContainer>
    </Section>
  )
}
